import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { formatDateToDDMMYYYY } from "../pages/const";

Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

const styles = StyleSheet.create({
  page: { padding: 20, fontFamily: "Times-Roman", fontSize: 9 },
  header: { marginBottom: 10, textAlign: "center" },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#8c1d1a",
    marginBottom: 4,
  },
  ledgerInfo: { fontSize: 10, marginBottom: 8, textAlign: "center" },
  table: {
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#8c1d1a",
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#8c1d1a",
    minHeight: 20,
  },
  tableHeaderRow: { backgroundColor: "#f0e6d2", fontWeight: "bold" },
  tableTotalRow: {
    backgroundColor: "#f0e6d2",
    fontWeight: "bold",
    borderTopWidth: 2,
    borderTopColor: "#8c1d1a",
  },
  cell: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "center",
  },
  cellLast: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 0,
    textAlign: "center",
  },
  cellRight: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "right",
  },
  cellLeft: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "left",
  },
  cellCredit: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "right",
    backgroundColor: "#e8f5ea",
    color: "#000",
  },
  cellDebit: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "right",
    backgroundColor: "#fce8ea",
    color: "#000",
  },
  cellBalance: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    textAlign: "right",
    backgroundColor: "#e8f4f7",
    color: "#000",
  },
  cellBalanceLast: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 0,
    textAlign: "right",
    backgroundColor: "#e8f4f7",
    color: "#000",
  },
  headerCell: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 4,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    backgroundColor: "#f0e6d2",
    color: "#000",
  },
  headerCellLast: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 4,
    textAlign: "center",
    borderRightWidth: 0,
    backgroundColor: "#f0e6d2",
    color: "#000",
  },
  openingBalanceRow: { backgroundColor: "#f8f9fa", fontWeight: "bold" },
  footer: { marginTop: 12, fontSize: 9, textAlign: "center" },
});

const CustomerDetailsPDF = ({
  ledgerData = [],
  customerInfo = {},
  fromDate,
  toDate,
  entireReport,
}) => {
  const name = customerInfo?.name || customerInfo?.fullName || "-";
  const phone = customerInfo?.mobile || customerInfo?.phone || "-";
  const city = customerInfo?.city || "-";
  const address = customerInfo?.address || "";

  const totals = ledgerData.reduce(
    (acc, row) => {
      const credit = parseFloat(row.credit) || 0;
      const debit = parseFloat(row.debit) || 0;
      return { credit: acc.credit + credit, debit: acc.debit + debit };
    },
    { credit: 0, debit: 0 }
  );

  const finalBalance =
    ledgerData.length > 0
      ? parseFloat(ledgerData[ledgerData.length - 1].balance || 0)
      : 0;

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">
        <View style={styles.header}>
          <Text style={styles.title}>Customer Ledger Report</Text>
        </View>
        <View style={styles.ledgerInfo}>
          <Text>
            Customer: {name} | Phone: {phone} | City: {city}
          </Text>
          {address ? <Text>{address}</Text> : null}
          {entireReport || fromDate || toDate ? (
            <Text>
              {entireReport
                ? "Entire Report"
                : `From: ${formatDateToDDMMYYYY(fromDate) || "-"} To: ${
                    formatDateToDDMMYYYY(toDate) || "-"
                  }`}
            </Text>
          ) : null}
        </View>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeaderRow]}>
            <Text style={[styles.headerCell, { width: "15%" }]}>DATE</Text>
            <Text style={[styles.headerCell, { width: "18%" }]}>TYPE</Text>
            <Text style={[styles.headerCell, { width: "15%" }]}>CREDIT</Text>
            <Text style={[styles.headerCell, { width: "15%" }]}>DEBIT</Text>
            <Text style={[styles.headerCell, { width: "15%" }]}>BALANCE</Text>
            <Text style={[styles.headerCellLast, { width: "22%" }]}>
              REMARKS
            </Text>
          </View>
          {ledgerData.map((row, idx) => (
            <View
              style={[
                styles.tableRow,
                row.isOpening && styles.openingBalanceRow,
              ]}
              key={idx}
            >
              <Text style={[styles.cell, { width: "15%" }]}>
                {row.isOpening ? "-" : row.date || "-"}
              </Text>
              <Text style={[styles.cellLeft, { width: "18%" }]}>
                {row.type || (row.isOpening ? "Opening Balance" : "-")}
              </Text>
              <Text style={[styles.cellCredit, { width: "15%" }]}>
                {row.credit || "-"}
              </Text>
              <Text style={[styles.cellDebit, { width: "15%" }]}>
                {row.debit || "-"}
              </Text>
              <Text style={[styles.cellBalance, { width: "15%" }]}>
                {row.balance || "-"}
              </Text>
              <Text style={[styles.cellLast, { width: "22%" }]}>
                {row.remarks || "-"}
              </Text>
            </View>
          ))}
          <View style={[styles.tableRow, styles.tableTotalRow]}>
            <Text style={[styles.cell, { width: "15%" }]}></Text>
            <Text style={[styles.cell, { width: "18%", fontWeight: "bold" }]}>
              TOTAL
            </Text>
            <Text
              style={[styles.cellCredit, { width: "15%", fontWeight: "bold" }]}
            >
              {totals.credit.toFixed(2)}
            </Text>
            <Text
              style={[styles.cellDebit, { width: "15%", fontWeight: "bold" }]}
            >
              {totals.debit.toFixed(2)}
            </Text>
            <Text
              style={[
                styles.cellBalanceLast,
                { width: "15%", fontWeight: "bold" },
              ]}
            >
              {finalBalance.toFixed(2)}
            </Text>
            <Text style={[styles.cellLast, { width: "22%" }]}></Text>
          </View>
        </View>
        <View style={styles.footer}>
          <Text>
            Generated on:{" "}
            {formatDateToDDMMYYYY(new Date().toISOString().split("T")[0])}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default CustomerDetailsPDF;
